import type { FilterTree, GeoOrigin } from "@/components/layout-2/filter-tree/types";
import { AnyDRQQuery } from "@/utils/drq";
import { DataService } from "./data";

/**
 * How a list is sorted: a column, or the distance of a location field (`column`, such as
 * `account__location`) from `origin`.
 */
export type ListViewOrderBy = { column: string; direction: "asc" | "desc"; origin?: GeoOrigin | null };

export interface ListView {
    id: number | string;
    name: string;
    filters: AnyDRQQuery | {};
    /** Advanced filters as the builder edits them: the saved tree, or the saved filters read back by the server. */
    filter_tree?: FilterTree | null;
    /** `advanced`: saved from the advanced editor, the list runs `filter_tree`; `classic`: the side panel edits `filters`. */
    filter_mode?: "classic" | "advanced";
    filter_logic?: string | null;
    columns: string[];
    order_by: ListViewOrderBy | null;
    created_by: {
        id: number;
        username: string;
        first_name: string;
        last_name: string;
    };
    reference_type: number;
    created_at: string;
    updated_at: string;
    is_pinned?: boolean;
    sharing_mode?: "private" | "protected" | "public";
    allow_other_edit?: boolean;
    can_user_edit?: boolean;
    charts?: ListViewChart[];
}

export type ChartType = "bar" | "horizontal_bar" | "line" | "pie";
export type ChartAggregation = "count" | "sum" | "avg";
export type ChartValueFormat = "number" | "currency" | "percentage";

export interface ListViewChart {
    id: number;
    name: string;
    chart_type: ChartType;
    aggregation: ChartAggregation;
    aggregate_field: string | null;
    group_by_field: string;
    position: number;
    value_format: ChartValueFormat;
    created_at?: string;
    updated_at?: string;
}

export interface CreateChartData {
    name: string;
    chart_type: ChartType;
    aggregation: ChartAggregation;
    aggregate_field?: string | null;
    group_by_field: string;
    position?: number;
    value_format?: ChartValueFormat;
}

export type UpdateChartData = Partial<CreateChartData>;

export interface ChartDataPoint {
    group: string | number | null;
    value: number;
}

export interface ChartDataResponse {
    chart_id: number;
    data: ChartDataPoint[];
}

export interface ListViewPin {
    id: number;
    user: {
        id: number;
        username: string;
        first_name: string;
        last_name: string;
    };
    list_view: number;
    pinned_at: string;
}

export interface CreateListViewData {
    name: string;
    filters: AnyDRQQuery | {};
    /** When sent, the server compiles it into `filters` (and clears `filter_logic`). */
    filter_tree?: FilterTree | null;
    filter_logic?: string | null;
    columns: string[];
    order_by: ListViewOrderBy | null;
    reference_type: number;
}

export interface UpdateListViewData {
    name?: string;
    filters?: AnyDRQQuery | {};
    filter_tree?: FilterTree | null;
    filter_logic?: string | null;
    columns?: string[];
    order_by?: ListViewOrderBy | null;
}

export const ListViewService = {
    list: (params?: Record<string, any>) =>
        DataService.client
            .url("/crm/app/list-views/")
            .query(params || {})
            .get()
            .json() as Promise<ListView[]>,

    create: (data: CreateListViewData) => DataService.client.url("/crm/app/list-views/").post(data).json() as Promise<ListView>,

    retrieve: (id: number | string) => DataService.client.url(`/crm/app/list-views/${id}/`).get().json() as Promise<ListView>,

    update: (id: number | string, data: UpdateListViewData) => DataService.client.url(`/crm/app/list-views/${id}/`).put(data).json() as Promise<ListView>,

    partialUpdate: (id: number | string, data: UpdateListViewData) => DataService.client.url(`/crm/app/list-views/${id}/`).patch(data).json() as Promise<ListView>,

    pin: (id: number | string) => DataService.client.url(`/crm/app/list-views/${id}/pin/`).post({}).json() as Promise<ListViewPin>,

    unpin: (id: number | string) => DataService.client.url(`/crm/app/list-views/${id}/unpin/`).post({}).json() as Promise<{ message: string }>,

    saveAs: (id: number | string, data: CreateListViewData) => DataService.client.url(`/crm/app/list-views/${id}/save_as/`).post(data).json() as Promise<ListView>,

    pinned: () => DataService.client.url("/crm/app/list-views/pinned/").get().json() as Promise<ListView[]>,

    rename: (id: number | string, name: string) => DataService.client.url(`/crm/app/list-views/${id}/`).patch({ name }).json() as Promise<ListView>,

    delete: (id: number | string) => DataService.client.url(`/crm/app/list-views/${id}/`).delete().res() as Promise<Response>,

    byReferenceType: (reference_type_id: number | string) => DataService.client.url("/crm/app/list-views/by_reference_type/").query({ reference_type_id }).get().json() as Promise<ListView[]>,

    // Sharing methods
    updateSharingMode: (id: number | string, sharing_mode: "private" | "protected" | "public") =>
        DataService.client.url(`/crm/app/list-views/${id}/update_sharing_mode/`).patch({ sharing_mode }).json() as Promise<ListView>,

    share: (id: number | string, data: { user_ids?: number[]; role_ids?: number[] }) => DataService.client.url(`/crm/app/list-views/${id}/share/`).post(data).json() as Promise<any>,

    getShares: (id: number | string) => DataService.client.url(`/crm/app/list-views/${id}/shares/`).get().json() as Promise<any[]>,

    unshare: (id: number | string) => DataService.client.url(`/crm/app/list-views/${id}/unshare/`).delete().json() as Promise<{ message: string }>,

    getPicklists: () => DataService.client.url("/crm/app/list-views/picklists/").get().json() as Promise<{ users: any[]; roles: any[] }>,

    // Charts (nested under a ListView)
    listCharts: (id: number | string) => DataService.client.url(`/crm/app/list-views/${id}/charts/`).get().json() as Promise<ListViewChart[]>,

    createChart: (id: number | string, data: CreateChartData) =>
        DataService.client.url(`/crm/app/list-views/${id}/charts/`).post(data).json() as Promise<ListViewChart>,

    updateChart: (id: number | string, chartId: number | string, data: UpdateChartData) =>
        DataService.client.url(`/crm/app/list-views/${id}/charts/${chartId}/`).patch(data).json() as Promise<ListViewChart>,

    deleteChart: (id: number | string, chartId: number | string) =>
        DataService.client.url(`/crm/app/list-views/${id}/charts/${chartId}/`).delete().res() as Promise<Response>,

    chartData: (id: number | string, chartId: number | string, params?: { limit?: number; query?: AnyDRQQuery | object | null; filterTree?: FilterTree | null }) => {
        // NB: backend reads `?filters=` (not `?query=`) on purpose so the
        // global DRQFilterBackend doesn't intercept this chart-scoped filter
        // and try to apply it to the parent ListView queryset.
        //
        // We send `?filters=` whenever a live query is provided -- INCLUDING
        // when it's an empty object. An empty live query means "the user
        // explicitly cleared all filters", which must override the ListView's
        // saved filters. Otherwise the backend would fall back to saved
        // filters and the chart would silently disagree with the now-empty
        // filter state shown in the table.
        const qs: Record<string, any> = {};
        if (params?.limit !== undefined) qs.limit = params.limit;
        if (params?.filterTree) {
            // Advanced filters travel in the body (empty = no filter, not « saved filters »).
            // A query string is repeated by the CORS preflight and dies above gunicorn's request-line cap.
            return DataService.client
                .url(`/crm/app/list-views/${id}/charts/${chartId}/data/`)
                .query(qs)
                .post({ live_filter_tree: params.filterTree.root.children.length ? params.filterTree : "" })
                .json() as Promise<ChartDataResponse>;
        }
        if (params?.query !== undefined && params?.query !== null) {
            qs.filters = JSON.stringify(params.query);
        }
        return DataService.client
            .url(`/crm/app/list-views/${id}/charts/${chartId}/data/`)
            .query(qs)
            .get()
            .json() as Promise<ChartDataResponse>;
    }
};
